/**
 * MOCK DATA — VER-56 Group Assessment Model A (Live Participant Check-In)
 *
 * All data is static stub only. No network calls are made from this module.
 * Replace each section with real API responses when the VER-55 backend is wired.
 *
 * Intended API contract is documented in:
 *   .claude/docs/features/VER-51/VER-56-NOTES.md
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** Unique token identifying a live group session. */
export type SessionCode = string;

/** Per-participant check-in status within a live session. */
export type ParticipantCheckInStatus = "waiting" | "in-progress" | "submitted";

/**
 * A single participant slot in a live group session roster.
 *
 * Identity is intentionally opaque: the `id` is a server-assigned UUID — it
 * is NEVER the participant's real name, email, or school ID. The `alias`
 * displayed in the live roster is a practitioner-visible label only for the
 * duration of the session (e.g. "Participant 3") and is not stored against
 * personal data. This satisfies the anonymity requirement from
 * docs/platform-upgrade-2026.md § Safety.
 */
export type LiveParticipant = {
    /** Opaque server UUID — never a personal identifier. */
    id: string;
    /**
     * Short practitioner-visible alias for this session only.
     * Pattern: "Participant N" or a practitioner-assigned label.
     * NOT persisted against personal data.
     */
    alias: string;
    status: ParticipantCheckInStatus;
    /** ISO timestamp of first join — null until participant opens the join URL. */
    joinedAt: string | null;
    /** ISO timestamp of WEMWBS form submission — null until submitted. */
    submittedAt: string | null;
};

/** Aggregate stats derived server-side. Individual scores are NEVER exposed. */
export type GroupAggregateSummary = {
    totalInvited: number;
    joined: number;
    submitted: number;
    /** Mean total WEMWBS score across all submitted responses. Rounded to 1dp. */
    meanScore: number | null;
    /**
     * Score band distribution — count per band only, no individual breakdown.
     * Bands: "low" (14-34), "moderate" (35-52), "high" (53-70).
     */
    bandCounts: {
        low: number;
        moderate: number;
        high: number;
    };
};

/** Full shape of a live group session. */
export type GroupLiveSession = {
    id: string;
    /** Human-readable 6-character session join code. */
    sessionCode: SessionCode;
    /** Display name for the session shown to both practitioner and participants. */
    name: string;
    /** ISO timestamp when the practitioner opened the session. */
    startedAt: string;
    /** "open" → accepting joins; "closed" → no new joins; "complete" → responses locked. */
    status: "open" | "closed" | "complete";
    participants: LiveParticipant[];
    aggregate: GroupAggregateSummary | null;
};

// ---------------------------------------------------------------------------
// Seed helpers
// ---------------------------------------------------------------------------

const now = new Date("2026-06-13T10:00:00.000Z");

function minutesAgo(n: number): string {
    return new Date(now.getTime() - n * 60 * 1000).toISOString();
}

// ---------------------------------------------------------------------------
// Mock session: "open" (live, accepting joins, partial submissions)
// ---------------------------------------------------------------------------

export const MOCK_LIVE_SESSION: GroupLiveSession = {
    id: "session-mock-001",
    sessionCode: "WB7X42",
    name: "Year 9 Social Wellbeing — Autumn Term Pre-Survey",
    startedAt: minutesAgo(8),
    status: "open",
    participants: [
        {
            id: "p-uuid-001",
            alias: "Participant 1",
            status: "submitted",
            joinedAt: minutesAgo(7),
            submittedAt: minutesAgo(5),
        },
        {
            id: "p-uuid-002",
            alias: "Participant 2",
            status: "submitted",
            joinedAt: minutesAgo(6),
            submittedAt: minutesAgo(4),
        },
        {
            id: "p-uuid-003",
            alias: "Participant 3",
            status: "in-progress",
            joinedAt: minutesAgo(4),
            submittedAt: null,
        },
        {
            id: "p-uuid-004",
            alias: "Participant 4",
            status: "waiting",
            joinedAt: minutesAgo(1),
            submittedAt: null,
        },
        {
            id: "p-uuid-005",
            alias: "Participant 5",
            status: "waiting",
            joinedAt: null,
            submittedAt: null,
        },
        {
            id: "p-uuid-006",
            alias: "Participant 6",
            status: "waiting",
            joinedAt: null,
            submittedAt: null,
        },
    ],
    // Aggregate is null while session is still open; populated when closed.
    aggregate: null,
};

// ---------------------------------------------------------------------------
// Mock session: "complete" (all submitted — aggregate available)
// ---------------------------------------------------------------------------

export const MOCK_COMPLETE_SESSION: GroupLiveSession = {
    id: "session-mock-002",
    sessionCode: "RT3K91",
    name: "Year 7 Literacy Circle — Spring Term Post-Survey",
    startedAt: minutesAgo(45),
    status: "complete",
    participants: [
        {
            id: "p-uuid-010",
            alias: "Participant 1",
            status: "submitted",
            joinedAt: minutesAgo(43),
            submittedAt: minutesAgo(40),
        },
        {
            id: "p-uuid-011",
            alias: "Participant 2",
            status: "submitted",
            joinedAt: minutesAgo(42),
            submittedAt: minutesAgo(38),
        },
        {
            id: "p-uuid-012",
            alias: "Participant 3",
            status: "submitted",
            joinedAt: minutesAgo(41),
            submittedAt: minutesAgo(36),
        },
        {
            id: "p-uuid-013",
            alias: "Participant 4",
            status: "submitted",
            joinedAt: minutesAgo(40),
            submittedAt: minutesAgo(35),
        },
    ],
    aggregate: {
        totalInvited: 4,
        joined: 4,
        submitted: 4,
        meanScore: 47.3,
        bandCounts: {
            low: 0,
            moderate: 3,
            high: 1,
        },
    },
};

// ---------------------------------------------------------------------------
// Initial state for the "initiate" screen form
// ---------------------------------------------------------------------------

export type InitiateFormValues = {
    sessionName: string;
    participantCount: number;
};

export const INITIATE_FORM_DEFAULTS: InitiateFormValues = {
    sessionName: "",
    participantCount: 10,
};
